import {wcfSelectAll} from '../lib/pagination.js';
import type {SupabaseErrorLike, SupabaseClient} from '../lib/supabaseTypes.js';

// Supabase production builders expose abortSignal(), while several focused
// loader fakes intentionally do not. Preserve those fakes and still hand the
// caller-provided signal to a real provider whenever it is available.
function withAbortSignal<Builder>(query: Builder, signal: AbortSignal | null): Builder {
  const abortable = query as Builder & {abortSignal?: (nextSignal: AbortSignal) => Builder};
  return signal && typeof abortable.abortSignal === 'function' ? abortable.abortSignal(signal) : query;
}

// Paginated daily-history loading (Pig sibling) rejects partial reads. It has
// the same underlying defect class as poultry dailys (see
// src/broiler/poultryDailysLoader.ts): wcfSelectAll deliberately swallows a
// page-read error and returns whatever it gathered before the failing page
// (see src/lib/pagination.js — CRITICAL, do not rewrite that shared helper),
// so a mid-pagination failure would silently publish a PARTIAL history as if
// it were the complete one. This loader captures the page error via closure
// and throws afterward, so the caller's own degrade-to-empty handling only
// ever sees "fully loaded" or "nothing usable, and here is why" — never a
// silently truncated result presented as complete.
//
// Rows are handed back exactly as `select('*')` produced them and this module
// reads no column of its own, so the row type stays `unknown` rather than
// asserting a schema TypeScript has no way to check.
export async function loadAllPigDailys(sb: SupabaseClient, signal: AbortSignal | null = null): Promise<unknown[]> {
  // A one-slot array rather than a `let`, because TypeScript's control-flow
  // analysis does not observe assignments made inside a callback: a
  // `let pageError: SupabaseErrorLike | null = null` still reads as `null` at
  // the check below, so `.message` would not exist on it. `.at(-1)` reproduces
  // the previous last-assignment-wins semantics exactly, and wcfSelectAll stops
  // at the first failing page so at most one error is ever pushed.
  const pageErrorSlot: SupabaseErrorLike[] = [];
  const rows = await wcfSelectAll(async (from, to) => {
    const query = sb
      .from('pig_dailys')
      .select('*')
      .is('deleted_at', null)
      .order('date', {ascending: false})
      .range(from, to);
    const res = await withAbortSignal(query, signal);
    if (res.error) pageErrorSlot.push(res.error);
    return res;
  });
  const pageError = pageErrorSlot.at(-1);
  if (pageError) throw new Error('pig_dailys: ' + (pageError.message || pageError));
  return rows;
}
